import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface HexagonTransitionProps {
  children: React.ReactNode;
  onComplete?: () => void;
}

interface TransitionState {
  active: boolean;
  x: number;
  y: number;
}

// Global state so any card can trigger it
let triggerTransition: ((x: number, y: number, cb: () => void) => void) | null = null;

export function useHexagonTransition() {
  const trigger = useCallback((e: React.MouseEvent, callback: () => void) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    if (triggerTransition) {
      triggerTransition(x, y, callback);
    } else {
      callback();
    }
  }, []);

  return trigger;
}

const HEX_CLIP = "polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)";

export function HexagonTransitionOverlay() {
  const [state, setState] = useState<TransitionState>({ active: false, x: 0, y: 0 });
  const [callback, setCallback] = useState<(() => void) | null>(null);

  useEffect(() => {
    triggerTransition = (x, y, cb) => {
      setState({ active: true, x, y });
      setCallback(() => cb);
    };
    return () => { triggerTransition = null; };
  }, []);

  const handleAnimationComplete = () => {
    if (callback) callback();
    // Reset after a short delay
    setTimeout(() => setState({ active: false, x: 0, y: 0 }), 50);
  };

  return (
    <AnimatePresence>
      {state.active && (
        <motion.div
          key="hex-transition"
          style={{
            position: "fixed",
            left: state.x - 40,
            top: state.y - 40,
            width: 80,
            height: 80,
            clipPath: HEX_CLIP,
            backgroundColor: "white",
            zIndex: 9999,
            transformOrigin: "center center",
            pointerEvents: "none",
          }}
          initial={{ scale: 0, opacity: 1 }}
          animate={{ scale: 60, opacity: 1 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          onAnimationComplete={handleAnimationComplete}
        />
      )}
    </AnimatePresence>
  );
}
